import React, { useEffect, useRef, useCallback } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-min-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/snippets/c_cpp.js';
import 'ace-builds/src-noconflict/mode-csharp';
import 'ace-builds/src-noconflict/snippets/csharp.js';
import 'ace-builds/src-noconflict/mode-golang';
import 'ace-builds/src-noconflict/snippets/golang.js';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/snippets/java.js';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/snippets/javascript.js';
import 'ace-builds/src-noconflict/mode-kotlin';
import 'ace-builds/src-noconflict/snippets/kotlin.js';
import 'ace-builds/src-noconflict/mode-php';
import 'ace-builds/src-noconflict/snippets/php.js';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/snippets/python.js';
import 'ace-builds/src-noconflict/mode-ruby';
import 'ace-builds/src-noconflict/snippets/ruby.js';
import 'ace-builds/src-noconflict/mode-scala';
import 'ace-builds/src-noconflict/snippets/scala.js';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/snippets/typescript.js';
import 'ace-builds/src-noconflict/mode-rust';
import 'ace-builds/src-noconflict/snippets/rust.js';
import 'ace-builds/src-noconflict/snippets/text.js';
import 'ace-builds/webpack-resolver';

import { Languages, categories } from 'util/appConstants';
import { useFreecodeContext, useAppContext } from 'hooks/useContext';
import { EditorContainer, CodeContainer, SolutionButtonContainer } from './styles';
import { codeAutoCompleter } from './codeAutoCompleter';
import { layouts } from 'components/FreecodePage/FreecodeLayoutConstants';
import { LanguageSelection } from 'components/FreecodeContent/FreecodeButtons';

export default function FreecodeEditorSolution() {
  const { layout, test, testImpl, language, languageData, code, setCode, isCreator } = useFreecodeContext();
  const { isDarkMode } = useAppContext();

  const smallScreenRef = useRef(null);
  const editorRef = useRef(null);

  const onChange = (value) => {
    if (!isCreator && test.category !== categories.CODE.category)
      localStorage.setItem(`fsEditor-${test.category}-${test._id}-${language}`, value);
    setCode(value);
  };

  const renderSmallScreenButtons = () => (
    <SolutionButtonContainer ref={smallScreenRef}>
      <LanguageSelection />
    </SolutionButtonContainer>
  );

  const getCompletions = useCallback(
    (_editor, session, pos, prefix, callback) => {
      codeAutoCompleter(session, pos, prefix, callback, testImpl?.generated?.autocompletions?.[language]);
    },
    [language, testImpl?.generated?.autocompletions]
  );

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.completers[0] = {
      getCompletions,
    };
  }, [getCompletions]);

  const onEditorLoad = (editor) => {
    editorRef.current = editor;
    if (editorRef.current.completers.length >= 4) return;
    let apiCompleter = {
      getCompletions,
    };
    editorRef.current.completers.unshift(apiCompleter);
  };

  return (
    <EditorContainer isDarkMode={isDarkMode}>
      {layout === layouts.small && renderSmallScreenButtons()}
      <CodeContainer smallScreenOffset={smallScreenRef.current?.clientHeight || 0}>
        <AceEditor
          placeholder="Please reset the code..."
          mode={Languages[language].EditorName}
          theme={isDarkMode ? 'monokai' : 'tomorrow'}
          fontSize={14}
          onLoad={onEditorLoad}
          onChange={onChange}
          value={code}
          name="solutionEditorId"
          showPrintMargin={false}
          wrapEnabled={true}
          width={'100%'}
          height={'100%'}
          editorProps={{ $blockScrolling: true }}
          enableBasicAutocompletion={true}
          enableLiveAutocompletion={true}
          enableSnippets={true}
          tabSize={languageData[language]?.TabSize || 4}
        />
      </CodeContainer>
    </EditorContainer>
  );
}
